import { Divider, Flex } from 'antd'
import { useState } from 'react'

import { usePersonsServiceGetInstitutionPersonListEndpoint } from 'src/api/queries'
import { Gender, PersonCaseStatus } from 'src/api/requests'
import { PageBreadcrumbs, RangePicker, SearchBar, Select } from 'src/components'
import { useTablePagination } from 'src/hooks'
import { disabledFutureDate, legalAgePickerValue } from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { PersonsTable } from '../components'

export const PersonsFromScreeningPage = () => {
  const { t } = useLocales()
  const pagination = useTablePagination()

  const [search, setSearch] = useState<string>()
  const [gender, setGender] = useState<string>()
  const [caseStatus, setCaseStatus] = useState<PersonCaseStatus>()
  const [shouldIncludeAnonymous, setShouldIncludeAnonymous] = useState(false)
  const [dateTo, setDateTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    ...(!!search && { searchTerm: search }),
    ...(!!gender && { gender: gender as Gender }),
    caseStatuses: caseStatus ? [caseStatus] : Object.values(PersonCaseStatus),
    ...(!!dateFrom &&
      !!dateTo && {
        birthDateFrom: dateFrom,
        birthDateTo: dateTo
      }),
    includeAnonymous: shouldIncludeAnonymous
  }

  const { data, isLoading } =
    usePersonsServiceGetInstitutionPersonListEndpoint(queryParams)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onGenderChange = (val: string) => {
    setGender(val)
    resetPagination(pagination)
  }

  const onCaseStatusChange = (val: PersonCaseStatus) => {
    setCaseStatus(val)
    resetPagination(pagination)
  }

  const onShouldIncludeAnonymousChange = (val: string) => {
    setShouldIncludeAnonymous(JSON.parse(val))
    resetPagination(pagination)
  }

  const genderOptions = [
    { value: 'male', label: t('form.male') },
    { value: 'female', label: t('form.female') },
    { value: 'unknown', label: t('form.unknown') }
  ]

  const anonymousOptions = [
    { value: 'true', label: t('form.include_anonymous') },
    { value: 'false', label: t('form.not_include_anonymous') }
  ]

  const caseStatusOptions = Object.values(PersonCaseStatus).map((value) => ({
    value,
    label: t(`persons.case_status.${value}`)
  }))

  return (
    <PageBreadcrumbs
      title={t('menu.persons_from_screening')}
      items={[{ title: t('menu.persons_from_screening') }]}
    >
      <Flex gap={16} wrap="wrap">
        <SearchBar
          maxWidth={330}
          onSearch={onSearch}
          placeholder={t('persons.idnp_name_surname_sime_code')}
        />

        <Select
          options={genderOptions}
          onChange={onGenderChange}
          placeholder={t('form.gender')}
          allowClear
        />

        <Select
          options={caseStatusOptions}
          value={caseStatus}
          onChange={onCaseStatusChange}
          placeholder={t('persons.case_status.title')}
          allowClear
        />

        <Select
          options={anonymousOptions}
          value={String(shouldIncludeAnonymous)}
          onChange={onShouldIncludeAnonymousChange}
        />

        <RangePicker
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          resetPagination={() => resetPagination(pagination)}
          disabledDate={disabledFutureDate}
          defaultPickerValue={legalAgePickerValue}
          placement="bottomRight"
          placeholder={[
            t('persons.from_date_of_birth'),
            t('persons.to_date_of_birth')
          ]}
        />
      </Flex>

      <Divider />

      <PersonsTable
        dataSource={data?.data}
        loading={isLoading}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
